import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { Observable } from 'rxjs';
import { Route, RouteParam } from '@web/common';
import {
  Icon,
  TableBuilderFactory,
  TableComponent,
  TableConfig,
  TableDataPayload,
  TableLazyLoad,
  TableService,
} from '@web/shared/ui';
import { Guid, once } from '@web/utils';
import { AdminBoxSaleCollectionFilter } from '../../models/admin-box-sale-collection-filter';
import { AdminBoxSaleCollectionItem } from '../../models/admin-box-sale-collection-item';
import { AdminBoxSaleListCollectionAction } from '../../enums/admin-box-sale-list-collection-action';
import { AdminBoxSaleListAction } from '../../enums/admin-box-sale-list-action';
import { AdminBoxSaleListItemAction } from '../../enums/admin-box-sale-list-item-action';
import { AdminBoxSaleDataSourceService } from '../../services/admin-box-sale-data-source.service';
import { AdminBoxSaleListCustomCellTemplateComponent } from '../../components/admin-box-sale-list-custom-cell-template/admin-box-sale-list-custom-cell-template.component';
import { AdminBoxSaleStatusProviderService } from '../../providers/admin-box-sale-status-provider.service';
import { AdminBoxSaleApproveModalComponent } from '../admin-box-sale-approve-modal/admin-box-sale-approve-modal.component';
import { AdminBoxSaleApproveStatus } from '../../models/admin-box-sale-approve-status';

@Component({
  selector: 'app-admin-box-sale-list',
  imports: [
    TableComponent,
    AdminBoxSaleListCustomCellTemplateComponent,
    AdminBoxSaleApproveModalComponent,
  ],
  templateUrl: './admin-box-sale-list.component.html',
  styleUrl: './admin-box-sale-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminBoxSaleListComponent implements OnInit {
  private readonly builder = inject(
    TableBuilderFactory<
      AdminBoxSaleCollectionItem,
      AdminBoxSaleCollectionFilter,
      AdminBoxSaleListItemAction,
      AdminBoxSaleListCollectionAction,
      AdminBoxSaleListAction
    >,
  ).create();
  private readonly dataSource = inject(AdminBoxSaleDataSourceService);
  private readonly statusProvider = inject(AdminBoxSaleStatusProviderService);
  private readonly table = inject(TableService);

  protected config!: TableConfig<
    AdminBoxSaleCollectionItem,
    AdminBoxSaleCollectionFilter,
    AdminBoxSaleListItemAction,
    AdminBoxSaleListCollectionAction,
    AdminBoxSaleListAction
  >;
  protected readonly approveModalVisible = signal(false);
  protected readonly approveStatus = signal<AdminBoxSaleApproveStatus | null>(
    null,
  );
  protected readonly approveStatusLoading = signal(false);
  protected readonly approveSubmitting = signal(false);

  private selectedApproveItemId: Guid | null = null;

  public ngOnInit(): void {
    this.buildTable();
  }

  private buildTable(): void {
    this.config = this.builder
      .dataKey('id')
      .itemActions((builder) => [
        builder
          .initRouterLink(AdminBoxSaleListItemAction.Preview)
          .visibility((item) => item.canBeViewed)
          .icon(Icon.Eye)
          .tooltip('Podgląd')
          .routerLink(
            (provider) => (item) =>
              provider.routerLink(Route.BoxSaleDetails, {
                [RouteParam.Guid]: item.id,
              }),
          )
          .build(),
        builder
          .initRouterLink(AdminBoxSaleListItemAction.Configure)
          .visibility((item) => item.canBeEdited)
          .icon(Icon.Pencil)
          .tooltip('Konfiguruj')
          .routerLink(
            (provider) => (item) =>
              provider.routerLink(Route.BoxSaleEdit, {
                [RouteParam.Guid]: item.id,
              }),
          )
          .build(),
        builder
          .initMore(AdminBoxSaleListItemAction.More)
          .visibility((item) => this.shouldVisibleMore(item))
          .children((builder) => [
            builder
              .initRouterLink(AdminBoxSaleListItemAction.PreviewBox)
              .visibility((item) => item.canBeBoxViewed)
              .icon(Icon.BoxSeam)
              .tooltip('Podgląd boxa')
              .routerLink(
                (provider) => (item) =>
                  provider.routerLink(Route.BoxSaleBoxDetails, {
                    [RouteParam.Guid]: item.boxId,
                  }),
              )
              .build(),
            builder
              .initCommand(AdminBoxSaleListItemAction.Approve)
              .visibility((item) => item.canBeApproved)
              .icon(Icon.CheckCircle)
              .tooltip('Zatwierdź')
              .command((item) => this.approve(item))
              .build(),
            builder
              .initCommand(AdminBoxSaleListItemAction.Archive)
              .visibility((item) => item.canBeArchived)
              .icon(Icon.Recycle)
              .tooltip('Zarchiwizuj')
              .command((item) => this.archive(item))
              .build(),
            builder
              .initCommand(AdminBoxSaleListItemAction.PublicContent)
              .visibility((item) => item.canPublicContent)
              .icon(Icon.GlobeAlt)
              .tooltip('Upublicznij zawartość')
              .command((item) => this.publicContent(item))
              .build(),
            builder
              .initCommand(AdminBoxSaleListItemAction.HideContent)
              .visibility((item) => item.canHideContent)
              .icon(Icon.ShieldLock)
              .tooltip('Ukryj zawartość')
              .command((item) => this.hideContent(item))
              .build(),
            builder
              .initCommand(AdminBoxSaleListItemAction.Remove)
              .visibility((item) => item.canBeRemoved)
              .icon(Icon.Trash)
              .tooltip('Usuń')
              .command((item) => this.remove(item))
              .build(),
          ])
          .build(),
      ])
      .filters((builder) => [
        builder.initText('boxName').build(),
        builder
          .initSelect('status')
          .dictionary(() => this.statusProvider.selectItems())
          .build(),
        builder
          .initSelect('isContentPublic')
          .dictionary(() => this.dataSource.booleanSelectItems())
          .build(),
      ])
      .columns((builder) => [
        builder
          .initCustom('featuredFileUrl')
          .width('9rem')
          .label('Zdjęcie główne')
          .build(),
        builder
          .initText('boxName')
          .label('Nazwa')
          .sortable(true)
          .sortKey('boxName')
          .filterable(true)
          .filterKey('boxName')
          .width('20rem')
          .build(),
        builder
          .initCustom('status')
          .label('Status')
          .sortable(true)
          .sortKey('status')
          .filterable(true)
          .filterKey('status')
          .width('14rem')
          .build(),
        builder
          .initCustom('isContentPublic')
          .label('Publiczna zawartość')
          .sortable(true)
          .sortKey('isContentPublic')
          .filterable(true)
          .filterKey('isContentPublic')
          .width('14rem')
          .build(),
      ])
      .dataSource({
        fetch: (
          lazyLoad: TableLazyLoad<AdminBoxSaleCollectionFilter>,
        ): Observable<TableDataPayload<AdminBoxSaleCollectionItem>> =>
          this.dataSource.fetchCollection(lazyLoad),
      })
      .build();
  }

  private shouldVisibleMore(item: AdminBoxSaleCollectionItem): boolean {
    return [
      item.canBeApproved,
      item.canBeArchived,
      item.canBeBoxViewed,
      item.canBeRemoved,
      item.canPublicContent,
      item.canHideContent,
    ].some((value) => value);
  }

  private archive(item: AdminBoxSaleCollectionItem): void {
    this.dataSource
      .confirmAndArchiveById(item.id)
      .pipe(once())
      .subscribe(() => this.table.refresh());
  }

  private publicContent(item: AdminBoxSaleCollectionItem): void {
    this.dataSource
      .confirmAndPublicContentById(item.id)
      .pipe(once())
      .subscribe(() => this.table.refresh());
  }

  private approve(item: AdminBoxSaleCollectionItem): void {
    this.selectedApproveItemId = item.id;
    this.approveStatus.set(null);
    this.approveStatusLoading.set(true);
    this.approveModalVisible.set(true);

    this.dataSource
      .fetchApproveStatusById(item.id)
      .pipe(once())
      .subscribe({
        next: (status) => {
          this.approveStatus.set(status);
          this.approveStatusLoading.set(false);
        },
        error: () => {
          this.handleApproveModalCancel();
        },
      });
  }

  private hideContent(item: AdminBoxSaleCollectionItem): void {
    this.dataSource
      .confirmAndHideContentById(item.id)
      .pipe(once())
      .subscribe(() => this.table.refresh());
  }

  private remove(item: AdminBoxSaleCollectionItem): void {
    this.dataSource
      .confirmAndRemoveById(item.id)
      .pipe(once())
      .subscribe(() => this.table.refresh());
  }

  protected handleApproveModalCancel(): void {
    this.selectedApproveItemId = null;
    this.approveModalVisible.set(false);
    this.approveStatusLoading.set(false);
    this.approveSubmitting.set(false);
  }

  protected handleApproveModalConfirm(): void {
    if (!this.selectedApproveItemId) {
      return;
    }

    this.approveSubmitting.set(true);
    this.dataSource
      .approveById(this.selectedApproveItemId)
      .pipe(once())
      .subscribe({
        next: () => {
          this.handleApproveModalCancel();
          this.table.refresh();
        },
        error: () => this.approveSubmitting.set(false),
      });
  }
}
